// Insights service for API calls to backend
import { authService } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface DailyBriefing {
  id: string;
  headline: string;
  summary: string;
  keyInsights: string[];
  riskHighlight: string;
  opportunityHighlight: string;
  actionableItems: string[];
  tone: string;
  generatedAt: string;
  confidence: number;
}

export interface RiskSummary {
  overallRiskLevel: string;
  primaryConcern: string;
  explanation: string;
  recommendations: string[];
  timeframe: string;
}

export interface SignalExplanation {
  signalId: string;
  userFriendlyTitle: string;
  explanation: string;
  portfolioImpact: string;
  actionGuidance: string;
  urgencyLevel: string;
}

class InsightsService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await authService.getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async getDailyBriefing(userId: string): Promise<DailyBriefing> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/insights/daily-briefing?user_id=${userId}`,
        {
          method: 'GET',
          headers
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.briefing;
    } catch (error) {
      console.error('Error fetching daily briefing:', error);
      throw error;
    }
  }

  async getRiskSummary(userId: string): Promise<RiskSummary> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/insights/risk-summary?user_id=${userId}`,
        {
          method: 'GET',
          headers
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.riskSummary;
    } catch (error) {
      console.error('Error fetching risk summary:', error);
      throw error;
    }
  }

  async getSignalExplanation(signalId: string, userId: string): Promise<SignalExplanation> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/insights/signal-explanation?signal_id=${signalId}&user_id=${userId}`,
        {
          method: 'GET',
          headers
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.explanation;
    } catch (error) {
      console.error('Error fetching signal explanation:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const insightsService = new InsightsService(); 